import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../layouts/AuthLayout';
import { AppLayout } from '../layouts/AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { NotFoundPage } from '../pages';

// Lazy-loaded feature pages.
const LoginPage = lazy(() =>
  import('@features/auth').then((m) => ({ default: m.LoginPage })),
);
const VerifyLoginPage = lazy(() =>
  import('@features/auth').then((m) => ({ default: m.VerifyLoginPage })),
);
const CardScanPage = lazy(() =>
  import('@features/cards').then((m) => ({ default: m.CardScanPage })),
);
const BillingNewPage = lazy(() =>
  import('@features/billing').then((m) => ({ default: m.BillingNewPage })),
);
const ClaimsPage = lazy(() => import('@features/claims').then((m) => ({ default: m.ClaimsPage })));
const SettlementsPage = lazy(() =>
  import('@features/reconciliation').then((m) => ({ default: m.SettlementsPage })),
);
const SettlementDetailPage = lazy(() =>
  import('@features/reconciliation').then((m) => ({ default: m.SettlementDetailPage })),
);
const MappingsPage = lazy(() =>
  import('@features/tariffs').then((m) => ({ default: m.MappingsPage })),
);
const TariffPricesPage = lazy(() =>
  import('@features/tariffs').then((m) => ({ default: m.TariffPricesPage })),
);
const TariffPricesDetailPage = lazy(() =>
  import('@features/tariffs').then((m) => ({ default: m.TariffPricesDetailPage })),
);
const TariffLinksPage = lazy(() =>
  import('@features/tariffs').then((m) => ({ default: m.TariffLinksPage })),
);
const TariffChangesPage = lazy(() =>
  import('@features/tariffs').then((m) => ({ default: m.TariffChangesPage })),
);
const PortfolioPage = lazy(() =>
  import('@features/portfolio').then((m) => ({ default: m.PortfolioPage })),
);
const SettingsPage = lazy(() => import('@features/settings').then((m) => ({ default: m.SettingsPage })));
const AuditPage = lazy(() => import('@features/audit').then((m) => ({ default: m.AuditPage })));
const AuditDetailPage = lazy(() => import('@features/audit').then((m) => ({ default: m.AuditDetailPage })));
const TeamPage = lazy(() => import('@features/team').then((m) => ({ default: m.TeamPage })));
const IntegrationsPage = lazy(() =>
  import('@features/integrations').then((m) => ({ default: m.IntegrationsPage })),
);
const AcceptInvitationPage = lazy(() =>
  import('@features/auth').then((m) => ({ default: m.AcceptInvitationPage })),
);

function SuspenseFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-screen text-sm text-[var(--color-text-secondary)]">
      {t('common.loading')}
    </div>
  );
}

const withSuspense = (node: ReactNode): ReactNode => (
  <Suspense fallback={<SuspenseFallback />}>{node}</Suspense>
);

/**
 * Application router configuration.
 *
 * The surface today: a public login and the public end of an invitation link, the
 * protected card-identification home, the billing screen it leads to, the
 * billings it sent, the reconciliation section, the tariff links and their changes, the portfolio, the
 * settings and the team, and a 404. No signup and no password reset (the first is
 * replaced by invitations, the second is planned; see the docs).
 */
export const router = createBrowserRouter(
  [
    // Auth routes (public).
    {
      element: <AuthLayout />,
      children: [
        { path: '/login', element: withSuspense(<LoginPage />) },
        // The emailed sign-in link (ADR-0022): public for the same reason as an
        // invitation, and it posts the token itself so a mail scanner burns nothing.
        { path: '/login/verify/:token', element: withSuspense(<VerifyLoginPage />) },
        // Public on purpose: whoever opens an invitation link may be signed in as
        // somebody else, or as nobody at all. The link belongs to the address it
        // was sent to, not to the browser it is opened in.
        { path: '/invitations/:token/accept', element: withSuspense(<AcceptInvitationPage />) },
      ],
    },

    // Protected routes — the app shell. The wildcard lives INSIDE the shell so a
    // signed-in user who mistypes a URL keeps the header (logout, language) and
    // the sidebar; `ProtectedRoute` bounces anonymous visitors to /login, where
    // the public wildcard below takes over.
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <AppLayout />,
          children: [
            { path: '/', element: withSuspense(<CardScanPage />) },
            { path: '/billing/new', element: withSuspense(<BillingNewPage />) },
            { path: '/facturas', element: withSuspense(<ClaimsPage />) },
            { path: '/conciliar', element: withSuspense(<SettlementsPage />) },
            { path: '/conciliar/:uuid', element: withSuspense(<SettlementDetailPage />) },
            { path: '/mapeos', element: withSuspense(<MappingsPage />) },
            { path: '/mapeos/:insurerSlug', element: withSuspense(<TariffLinksPage />) },
            { path: '/tarifas', element: withSuspense(<TariffPricesPage />) },
            { path: '/tarifas/:insurerSlug', element: withSuspense(<TariffPricesDetailPage />) },
            { path: '/tarifas/:insurerSlug/cambios', element: withSuspense(<TariffChangesPage />) },
            { path: '/cartera', element: withSuspense(<PortfolioPage />) },
            { path: '/ajustes', element: withSuspense(<SettingsPage />) },
            { path: '/ajustes/auditoria', element: withSuspense(<AuditPage />) },
            { path: '/ajustes/auditoria/:uuid', element: withSuspense(<AuditDetailPage />) },
            { path: '/ajustes/equipo', element: withSuspense(<TeamPage />) },
            { path: '/ajustes/integraciones', element: withSuspense(<IntegrationsPage />) },
            { path: '*', element: <NotFoundPage /> },
          ],
        },
      ],
    },

    // 404 for anonymous visitors (a bare page, no shell to show).
    { path: '*', element: <NotFoundPage /> },
  ],
  {
    future: { v7_relativeSplatPath: true },
  },
);
